import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Email as EmailIcon,
  Person as PersonIcon,
  Remove as RemoveIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Cancel as CancelIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import api from '../api';

const ClientPortalStatus = ({ clientId, onInvitationSent, onInviteClick }) => {
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [selectedInvitation, setSelectedInvitation] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    if (clientId) {
      fetchInvitations();
    }
  }, [clientId]);

  const fetchInvitations = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/clients/${clientId}/invitations/`);
      if (response.status === 200) {
        // The API returns data in response.data.data
        const invitationsData = response.data.data || response.data;
        setInvitations(invitationsData);
      } else {
        setError(response.error || 'Failed to load invitations');
      }
    } catch (err) {
      setError('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveAccess = async (invitation) => {
    setSelectedInvitation(invitation);
    setRemoveDialogOpen(true);
  };

  const confirmRemoveAccess = async () => {
    if (!selectedInvitation) return;

    try {
      setActionLoading(true);
      const response = await api.delete(`/api/invitations/${selectedInvitation.id}/`);
      
      if (response.success) {
        // Refresh invitations list
        await fetchInvitations();
        setRemoveDialogOpen(false);
        setSelectedInvitation(null);
      } else {
        setError(response.error || 'Failed to remove access');
      }
    } catch (err) {
      setError('An unexpected error occurred');
    } finally {
      setActionLoading(false);
    }
  };

  const handleResendInvite = async (invitation) => {
    try {
      setActionLoading(true);
      setError('');
      const response = await api.post(`/api/invitations/${invitation.id}/`, {});
      const data = response.data || response;
      if (data.success) {
        await fetchInvitations();
      } else {
        setError(data.error || 'Failed to resend invitation');
      }
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to resend invitation');
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusIcon = (invitation) => {
    // If client user exists, show different icon based on last login
    if (invitation.client_user_exists) {
      if (invitation.last_login) {
        return <CheckCircleIcon color="success" />;
      } else {
        return <PersonIcon color="info" />;
      }
    }
    
    // If no client user, show invitation status
    switch (invitation.status) {
      case 'accepted':
        return <CheckCircleIcon color="success" />;
      case 'sent':
        return <ScheduleIcon color="warning" />;
      case 'expired':
        return <CancelIcon color="error" />;
      case 'cancelled':
        return <CancelIcon color="error" />;
      default:
        return <ScheduleIcon color="default" />;
    }
  };

  const getStatusColor = (invitation) => {
    // If client user exists, show different color based on last login
    if (invitation.client_user_exists) {
      if (invitation.last_login) {
        return 'success';
      } else {
        return 'info';
      }
    }
    
    // If no client user, show invitation status color
    switch (invitation.status) {
      case 'accepted':
        return 'success';
      case 'sent':
        return 'warning';
      case 'expired':
        return 'error';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (invitation) => {
    // If client user exists, show account status
    if (invitation.client_user_exists) {
      if (invitation.last_login) {
        return 'Logged in';
      } else {
        return 'Account created';
      }
    }
    
    // If no client user, show invitation status
    switch (invitation.status) {
      case 'accepted':
        return 'Accepted';
      case 'sent':
        return 'Invitation sent';
      case 'expired':
        return 'Expired';
      case 'cancelled':
        return 'Cancelled';
      default:
        return invitation.status;
    }
  };

  const formatLastLogin = (lastLogin) => {
    if (!lastLogin) return null;
    
    const date = new Date(lastLogin);
    const now = new Date();
    const diffTime = now - date;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor(diffTime / (1000 * 60 * 60));
    const diffMinutes = Math.floor(diffTime / (1000 * 60));
    
    // Handle future dates (shouldn't happen but just in case)
    if (diffTime < 0) {
      return 'Just now';
    }
    
    // Less than 1 minute
    if (diffMinutes < 1) {
      return 'Just now';
    }
    // Less than 1 hour
    else if (diffMinutes < 60) {
      return diffMinutes === 1 ? '1 minute ago' : `${diffMinutes} minutes ago`;
    }
    // Less than 1 day
    else if (diffHours < 24) {
      return diffHours === 1 ? '1 hour ago' : `${diffHours} hours ago`;
    }
    // 1 day
    else if (diffDays === 1) {
      return '1 day ago';
    }
    // 2-6 days
    else if (diffDays < 7) {
      return `${diffDays} days ago`;
    }
    // 1-3 weeks
    else if (diffDays < 30) {
      const weeks = Math.floor(diffDays / 7);
      return weeks === 1 ? '1 week ago' : `${weeks} weeks ago`;
    }
    // 1-11 months
    else if (diffDays < 365) {
      const months = Math.floor(diffDays / 30);
      return months === 1 ? '1 month ago' : `${months} months ago`;
    }
    // Years
    else {
      const years = Math.floor(diffDays / 365);
      return years === 1 ? '1 year ago' : `${years} years ago`;
    }
  };

  if (loading) {
    return (
      <Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6" color="primary" sx={{ fontWeight: 'bold' }}>
            Client Portal
          </Typography>
          <Box display="flex" gap={1}>
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={onInviteClick}
            >
              Invite
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<RefreshIcon />}
              onClick={fetchInvitations}
            >
              Refresh
            </Button>
          </Box>
        </Box>
        <Box display="flex" justifyContent="center" alignItems="center" py={4}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6" color="primary" sx={{ fontWeight: 'bold' }}>
            Client Portal
          </Typography>
          <Box display="flex" gap={1}>
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={onInviteClick}
            >
              Invite
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<RefreshIcon />}
              onClick={fetchInvitations}
            >
              Refresh
            </Button>
          </Box>
        </Box>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6" color="primary" sx={{ fontWeight: 'bold' }}>
          Client Portal
        </Typography>
        <Box display="flex" gap={1}>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={onInviteClick}
          >
            Invite
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<RefreshIcon />}
            onClick={fetchInvitations}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {(invitations || []).length === 0 ? (
        <Card>
          <CardContent>
            <Box textAlign="center" py={2}>
              <EmailIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
              <Typography variant="h6" color="text.secondary">
                No invitations sent
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Send an invitation to give this client access to the portal
              </Typography>
            </Box>
          </CardContent>
        </Card>
      ) : (
        <Box>
          {(invitations || []).map((invitation) => (
            <Card key={invitation.id} sx={{ mb: 2, cursor: 'pointer', '&:hover': { boxShadow: 3 } }}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                  <Box flex={1}>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <Typography variant="h6" fontWeight="medium">
                        {invitation.client_name || invitation.email}
                      </Typography>
                      <Chip
                        label={invitation.is_spouse ? 'Spouse' : 'Client'}
                        size="small"
                        color={invitation.is_spouse ? 'secondary' : 'primary'}
                      />
                    </Box>
                    
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      {getStatusIcon(invitation)}
                      <Typography 
                        variant="body2" 
                        color={invitation.client_user_exists && invitation.last_login ? 'success.main' : 'text.secondary'}
                        fontWeight={invitation.client_user_exists && invitation.last_login ? 'medium' : 'normal'}
                      >
                        {invitation.client_user_exists ? (
                          invitation.last_login ? 
                            `Last login: ${formatLastLogin(invitation.last_login)}` :
                            'Account created but never logged in'
                        ) : (
                          `Invite sent, not accepted`
                        )}
                      </Typography>
                    </Box>
                  </Box>
                  
                  <Box display="flex" alignItems="center" gap={0.5}>
                    {invitation.client_user_exists ? (
                      <>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleResendInvite(invitation);
                          }}
                          disabled={actionLoading}
                          title="Resend invite"
                        >
                          <RefreshIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveAccess(invitation);
                          }}
                          disabled={actionLoading}
                          color="error"
                          title="Remove access"
                        >
                          <RemoveIcon />
                        </IconButton>
                      </>
                    ) : (
                      <>
                        {invitation.status === 'sent' && (
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleResendInvite(invitation);
                            }}
                            disabled={actionLoading}
                            title="Resend invite"
                          >
                            <RefreshIcon />
                          </IconButton>
                        )}
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveAccess(invitation);
                          }}
                          disabled={actionLoading}
                          color="error"
                          title="Remove access"
                        >
                          <RemoveIcon />
                        </IconButton>
                      </>
                    )}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      {/* Remove Access Confirmation Dialog */}
      <Dialog open={removeDialogOpen} onClose={() => setRemoveDialogOpen(false)}>
        <DialogTitle>Remove Access</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to remove portal access for {selectedInvitation?.client_name || selectedInvitation?.email}?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoveDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={confirmRemoveAccess}
            color="error"
            variant="contained"
            disabled={actionLoading}
          >
            {actionLoading ? <CircularProgress size={20} /> : 'Remove Access'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClientPortalStatus;
